/**
 * Crossword Mini — Word search puzzle utilities (Flutter VM parity).
 * Same daily seed string, RNG, placement, lengths, bonuses as
 * `letter_quest/lib/features/crossword_mini/crossword_mini_logic.dart`.
 */

import { GAME_WORDS } from '../../data/gameWords';
import { DartRandom } from '../../utils/dartRandom';

/** `YYYY-MM-DD` local — Firestore streak keys (`dateKeyForFirestore` on Flutter). */
export function getDateKey(d = new Date()) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** `YYYY-M-D` unpadded — matches `WordMasterWordUtils.dateKeyForHash`. */
export function dateKeyForHash(d = new Date()) {
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

/** Matches `WordMasterWordUtils.hashStringJs32` / JS `hash | 0` per char. */
export function hashStringJs32(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    const c = str.charCodeAt(i);
    h = ((h << 5) - h + c) | 0;
  }
  return h;
}

/** Same formula as Flutter `crosswordDailySeed`. */
export function getDailyCrosswordSeed(d = new Date()) {
  const key = dateKeyForHash(d);
  const h = hashStringJs32(`crossword-mini-${key}`);
  return Math.abs(h);
}

let _crosswordWordPool = null;

function crosswordCandidatePool() {
  if (!_crosswordWordPool) {
    _crosswordWordPool = [...GAME_WORDS].filter((w) => w.length >= 4 && w.length <= 8);
  }
  return _crosswordWordPool;
}

function shuffleInPlace(arr, rng) {
  const n = arr.length;
  for (let i = n - 1; i > 0; i--) {
    const j = rng.nextInt(i + 1);
    const t = arr[i];
    arr[i] = arr[j];
    arr[j] = t;
  }
}

function wordsForPuzzle(seed, count, minLen, maxLen) {
  const rng = new DartRandom(seed);
  const pool = crosswordCandidatePool().filter((w) => w.length >= minLen && w.length <= maxLen);
  const copy = [...pool];
  shuffleInPlace(copy, rng);
  if (copy.length <= count) return copy;
  return copy.slice(0, count);
}

function canPlace(cells, rows, cols, word, r, c, dr, dc) {
  for (let i = 0; i < word.length; i++) {
    const rr = r + dr * i;
    const cc = c + dc * i;
    if (rr < 0 || rr >= rows || cc < 0 || cc >= cols) return false;
    const idx = rr * cols + cc;
    const ch = cells[idx];
    const wch = word[i];
    if (ch !== '.' && ch !== wch) return false;
  }
  return true;
}

function placeWord(cells, word, r, c, dr, dc, cols) {
  for (let i = 0; i < word.length; i++) {
    const idx = (r + dr * i) * cols + (c + dc * i);
    cells[idx] = word[i];
  }
}

function pathFor(r, c, dr, dc, len, cols) {
  return Array.from({ length: len }, (_, i) => (r + dr * i) * cols + (c + dc * i));
}

function tryGenerateWordSearch(wordsSorted, rows, cols, seed, maxWords) {
  const rng = new DartRandom(seed);
  const cells = Array(rows * cols).fill('.');
  /** @type {Array<{ word: string, indices: number[] }>} */
  const placed = [];

  for (const word of wordsSorted) {
    if (placed.length >= maxWords) break;
    let placedThis = false;
    for (let t = 0; t < 240 && !placedThis; t++) {
      const dr = rng.nextInt(3) - 1;
      const dc = rng.nextInt(3) - 1;
      if (dr === 0 && dc === 0) continue;
      const rr = rng.nextInt(rows);
      const cc = rng.nextInt(cols);
      if (canPlace(cells, rows, cols, word, rr, cc, dr, dc)) {
        placeWord(cells, word, rr, cc, dr, dc, cols);
        placed.push({
          word,
          indices: pathFor(rr, cc, dr, dc, word.length, cols),
        });
        placedThis = true;
      }
    }
  }

  if (placed.length < 3) return null;

  for (let i = 0; i < cells.length; i++) {
    if (cells[i] === '.') {
      cells[i] = String.fromCharCode(65 + rng.nextInt(26));
    }
  }

  const grid = [];
  for (let r = 0; r < rows; r++) {
    const row = [];
    for (let c = 0; c < cols; c++) {
      row.push(cells[r * cols + c]);
    }
    grid.push(row);
  }

  const hiddenWords = placed.map((p) => p.word);
  const wordPositions = new Map();
  for (const p of placed) {
    wordPositions.set(
      p.word,
      p.indices.map((idx) => [Math.floor(idx / cols), idx % cols]),
    );
  }

  return { grid, hiddenWords, wordPositions };
}

function fallbackPuzzle(rows, cols, seed) {
  const rng = new DartRandom(seed);
  const cells = Array(rows * cols).fill('.');
  const words = ['CAT', 'DOG', 'SUN'];
  /** @type {Array<{ word: string, indices: number[] }>} */
  const placed = [];
  const colsN = cols;
  for (let wi = 0; wi < words.length; wi++) {
    const w = words[wi];
    const dr = 0;
    const dc = 1;
    const r = wi;
    const c = 0;
    if (wi >= rows || w.length > colsN) continue;
    if (canPlace(cells, rows, colsN, w, r, c, dr, dc)) {
      placeWord(cells, w, r, c, dr, dc, colsN);
      placed.push({
        word: w,
        indices: pathFor(r, c, dr, dc, w.length, colsN),
      });
    }
  }
  for (let i = 0; i < cells.length; i++) {
    if (cells[i] === '.') {
      cells[i] = String.fromCharCode(65 + rng.nextInt(26));
    }
  }
  const grid = [];
  for (let rr = 0; rr < rows; rr++) {
    const row = [];
    for (let cc = 0; cc < colsN; cc++) {
      row.push(cells[rr * colsN + cc]);
    }
    grid.push(row);
  }
  const hiddenWords = placed.map((p) => p.word);
  const wordPositions = new Map();
  for (const p of placed) {
    wordPositions.set(
      p.word,
      p.indices.map((idx) => [Math.floor(idx / colsN), idx % colsN]),
    );
  }
  return { grid, hiddenWords, wordPositions };
}

/**
 * Build a word-search grid (Flutter `generateWordSearchPuzzle` parity).
 * @param {number} size rows / cols (daily uses 8)
 * @param {number} seed
 */
export function generateWordSearch(size, seed) {
  const rows = size;
  const cols = size;
  const maxLen = Math.min(8, Math.max(rows, cols));
  const pool = wordsForPuzzle(seed, 48, 4, maxLen);
  const sorted = [...pool].sort((a, b) => b.length - a.length);

  for (let offset = 0; offset < 40; offset++) {
    const p = tryGenerateWordSearch(sorted, rows, cols, seed + offset, 8);
    if (p) return p;
  }
  return fallbackPuzzle(rows, cols, seed);
}

export function getDailyCrosswordConfig(d = new Date()) {
  return { size: 8, seed: getDailyCrosswordSeed(d) };
}

export function isValidCrosswordWord(word) {
  const w = (word || '').toUpperCase().trim();
  return w.length >= 4 && w.length <= 8 && GAME_WORDS.has(w);
}

/**
 * Locked line direction after two cells (Flutter `_dr` / `_dc`).
 * @typedef {{ dr: number, dc: number } | null} CrosswordLineDir
 */

/**
 * Tap path for word search — mirrors Flutter `CrosswordMiniScreen._onCrosswordCellTap`.
 *
 * @param {Array<[number, number]>} prevSelection
 * @param {number} cols
 * @param {number} tapR
 * @param {number} tapC
 * @param {CrosswordLineDir} lineDir
 * @returns {{ selection: Array<[number, number]>, lineDir: CrosswordLineDir }}
 */
export function tapCrosswordLineSelection(prevSelection, cols, tapR, tapC, lineDir) {
  const idx = tapR * cols + tapC;

  if (prevSelection.length === 0) {
    return { selection: [[tapR, tapC]], lineDir: null };
  }

  const last = prevSelection[prevSelection.length - 1];
  const [lr, lc] = last;
  const lastIdx = lr * cols + lc;

  if (idx === lastIdx) {
    const next = prevSelection.length > 1 ? prevSelection.slice(0, -1) : [];
    const nextDir = next.length <= 1 ? null : lineDir;
    return { selection: next, lineDir: nextDir };
  }

  const inPath = prevSelection.some(([r, c]) => r * cols + c === idx);
  if (inPath) {
    return { selection: prevSelection, lineDir };
  }

  if (prevSelection.length === 1) {
    const [r0, c0] = prevSelection[0];
    if (Math.abs(tapR - r0) > 1 || Math.abs(tapC - c0) > 1) {
      return { selection: [[tapR, tapC]], lineDir: null };
    }
    const dr = Math.sign(tapR - r0);
    const dc = Math.sign(tapC - c0);
    if (dr === 0 && dc === 0) {
      return { selection: prevSelection, lineDir };
    }
    return {
      selection: [...prevSelection, [tapR, tapC]],
      lineDir: { dr, dc },
    };
  }

  if (lineDir == null) {
    return { selection: [[tapR, tapC]], lineDir: null };
  }
  const expR = lr + lineDir.dr;
  const expC = lc + lineDir.dc;
  if (tapR === expR && tapC === expC) {
    return { selection: [...prevSelection, [tapR, tapC]], lineDir };
  }
  return { selection: [[tapR, tapC]], lineDir: null };
}

/** Check if selection spells a hidden word (forward or reverse) and matches positions */
export function findMatchingWord(selection, grid, wordPositions) {
  const spelled = selection.map(([r, c]) => grid[r][c]).join('');
  const upper = spelled.toUpperCase();
  const reversed = upper.split('').reverse().join('');
  const selSet = new Set(selection.map(([r, c]) => `${r}-${c}`));

  for (const [word, positions] of wordPositions.entries()) {
    if (word !== upper && word !== reversed) continue;
    const posSet = new Set(positions.map(([r, c]) => `${r}-${c}`));
    if (selSet.size === posSet.size && [...selSet].every((k) => posSet.has(k))) {
      return word;
    }
  }
  return null;
}

/** Flutter `crosswordMiniMaxHintsPerPuzzle` */
export const crosswordMiniMaxHintsPerPuzzle = 3;

/** Flutter `crosswordScoreForWord` */
export function scoreForWord(word) {
  const len = (word || '').length;
  return len >= 4 && len <= 8 ? len * 5 : 0;
}

/**
 * Points when submitting a hidden word (Flutter `crosswordScoreForFoundWord`).
 * Hint-affected words earn half (floored), minimum 5 points.
 *
 * @param {string} word matched hidden word (as from generator, upper case typical)
 * @param {boolean} hadRevealHint whether the player used a hint reveal on this word before finding it
 * @returns {number}
 */
export function scoreForFoundWord(word, hadRevealHint) {
  const base = scoreForWord(word);
  if (!hadRevealHint) return base;
  return Math.max(5, Math.floor(base / 2));
}

export const crosswordAllWordsBonus = () => 50;

const STAR_THRESHOLDS = {
  6: [30, 60, 100],
  7: [50, 90, 150],
  8: [60, 120, 190],
};

export function getStarCount(score, size) {
  const [s1, s2, s3] = STAR_THRESHOLDS[size] || STAR_THRESHOLDS[8];
  if (score >= s3) return 3;
  if (score >= s2) return 2;
  if (score >= s1) return 1;
  return 0;
}
